import { StateCreator } from 'zustand';

import insureAPIs, {
  payoptionsTransform,
  productFeaturesTransform,
  suggestionTransform
} from '@services/apis/insure';
import { TERM_FAST_QUOTE_STEPS } from '@termquote/constants';
import { cleanData } from '@utils';

import { ProductSliceState, TermQuoteStoreState } from './types';

export const productSlice: StateCreator<
  TermQuoteStoreState,
  [['zustand/immer', never], ['zustand/devtools', never], never],
  [],
  ProductSliceState
> = (set, get) => ({
  loadingProductBoundaries: false,
  loadingProductFeatures: false,
  productBoundariesError: '',
  productsList: [],
  paymentTermOptions: [],
  loadingPaymentTermOptions: false,
  // fetch and store product boundaries in store
  fetchProductBoundaries: async (products: string[], retainFeatures = false) => {
    const productsList = get().productsList;
    const plan = get().plan;

    set((state) => {
      state.loadingProductBoundaries = true;
      state.productBoundariesError = '';
    });
    try {
      const productBoundariesResponse = await insureAPIs.getProductBoundaries({
        products,
        ...cleanData({
          ...suggestionTransform(plan),
          cover: plan.cover ? plan.cover : undefined,
          tillAge: plan.tillAge ? plan.tillAge : undefined,
          paymentType: plan.payOption ? plan.payOption : undefined
        })
      });
      const latestProductList = productBoundariesResponse.data;
      if (!latestProductList.length) {
        set((state) => {
          state.loadingProductBoundaries = false;
          state.productBoundariesError =
            'Failed to load quote for this product, please try again later.';
        });
        return;
      }
      /**
       * If retainFeatures is set, retain the prev values for features from the
       * existing list of products, this is done to avoid features api call in case
       * user changes plan details like payment frequency which doesn't have any
       * effect on the features.
       */
      if (retainFeatures && productsList.length) {
        productsList.forEach((productItem) => {
          const current = latestProductList.findIndex(
            (item) => item.product.key === productItem.product.key
          );
          if (current !== -1) {
            latestProductList[current] = {
              ...latestProductList[current],
              boundaries: {
                ...latestProductList[current].boundaries,
                mainBenefits: productItem.boundaries.mainBenefits,
                features: productItem.boundaries.features,
                cardFeatures: productItem.boundaries.cardFeatures
              }
            };
          }
        });
      }

      set((state) => {
        state.loadingProductBoundaries = false;
        state.productsList = latestProductList;
      });
    } catch (err) {
      set((state) => {
        state.loadingProductBoundaries = false;
        state.productBoundariesError =
          products.length === 1
            ? 'Failed to fetch product details'
            : 'Failed to fetch recommended plans details';
      });
    }
  },
  // For a given boundary dependency, if the current product has the dependency
  // then fetch the product boundaries again
  refetchProductBoundaries: async (boundaryDependency) => {
    if (!boundaryDependency) return;

    if (!get().plan.product) return;

    const currentProductBoundary = get().productsList?.find(
      (product) => product.product.key === get().plan.product
    )?.boundaries;

    if (!currentProductBoundary) {
      return;
    }

    if (
      Array.isArray(currentProductBoundary.dependencies) &&
      currentProductBoundary.dependencies.includes(boundaryDependency)
    ) {
      await get().fetchProductBoundaries([get().plan.product], true);
    }
  },
  // fetch and store product features in store
  fetchProductFeatures: async (products: string[]) => {
    // proceed only if products are present
    if (products.length) {
      set((state) => {
        state.loadingProductFeatures = true;
      });
      const plan = get().plan;
      try {
        const productFeaturesResponse = await insureAPIs.getProductFeatures(
          productFeaturesTransform(products, plan)
        );

        const latestProductList = [...get().productsList];

        // update existing productList with latest values
        productFeaturesResponse.data.map((productFeatures) => {
          const current = latestProductList.findIndex(
            (item) => item.product.key === productFeatures.product
          );
          if (current !== -1) {
            latestProductList[current] = {
              ...latestProductList[current],
              boundaries: {
                ...latestProductList[current].boundaries,
                mainBenefits: productFeatures.mainBenefits,
                features: productFeatures.features,
                cardFeatures: productFeatures.cardFeatures
              }
            };
          }
        });

        set((state) => {
          state.loadingProductFeatures = false;
          state.productsList = latestProductList;
        });
      } catch (err) {
        set((state) => {
          state.loadingProductFeatures = false;
          state.productBoundariesError =
            products.length === 1
              ? 'Failed to fetch product details'
              : 'Failed to fetch recommended plans details';
        });
      }
    }
  },
  fetchPaymentTermOptions: async () => {
    const plan = get().plan;
    set((state) => {
      state.loadingPaymentTermOptions = true;
    });
    const paymentOptionsResponse = await insureAPIs.getTermPayOptions(
      payoptionsTransform(plan)
    );

    if (get().step === TERM_FAST_QUOTE_STEPS.PLAN) {
      // mark the recommended option
      const { payOptions, recommended } = paymentOptionsResponse.data;
      const payOptionsWithRecommended = payOptions.map((item) => {
        if (item.key === recommended) {
          item.recommended = true;
        }
        return item;
      });

      set((state) => {
        state.loadingPaymentTermOptions = false;
        state.paymentTermOptions = payOptionsWithRecommended;
      });

      // Set the recommended option as the selected one
      const currentPayOption = get().plan.payOption;
      const recommendedPayOption = payOptions.find(
        (payOption) => payOption.key === recommended
      );
      const currentPayOptionItem = payOptions.find(
        (payOption) => payOption.key === currentPayOption
      );
      if (currentPayOption === '' && recommendedPayOption) {
        get().setUserPayOption(recommendedPayOption);
      } else if (currentPayOptionItem) {
        get().setUserPayOption(currentPayOptionItem);
      }
    } else {
      set((state) => {
        state.loadingPaymentTermOptions = false;
        state.paymentTermOptions = [];
      });
    }
  },
  clearPaymentTermOptions: () => {
    set((state) => {
      state.paymentTermOptions = [];
      // clear addons too
      state.addOns = [];
    });
  }
});
